import { css } from "@emotion/css";
import type { PageAction, SimpleMenuItem } from "@octopusdeploy/design-system-components";
import { ActionButton, ActionButtonType, Breadcrumbs, Button, Callout, Checkbox, Dialog, SimpleDataTable, useShowSnackbar } from "@octopusdeploy/design-system-components";
import { CodeCommitIcon } from "@octopusdeploy/design-system-icons";
import { Avatar } from "@octopusdeploy/design-system-octopus-components";
import { space, text, themeTokens } from "@octopusdeploy/design-system-tokens";
import { useAggregateAPIOperationStatus, useMutation, useQuery } from "@octopusdeploy/octopus-react-client";
import type { ActionTemplateParameterResource, ActionTemplateResource, DeploymentActionResource, DeploymentStepResource, PlatformHubVersionControlSettingsResource, GitRef, GitRefResource, IconMetadataResource, IconSvgResource, ModifyProcessTemplateCommand, ProcessTemplateParameterResource, ProcessTemplateResource, ProcessTemplateVersionResource, CreateProcessTemplateVersionCommand, Repository, ShareProcessTemplateCommand, SpaceResource, ValidateGitRefV2Response, } from "@octopusdeploy/octopus-server-client";
import { isImportableValueControlType, ControlType, IsDefaultBranch, isProcessTemplateParameterResource, isProtectedBranch, mapFromEnvironmentResourceToEnvironmentV2Resource, OctopusError, Permission, ProcessType, ValidateGitRefV2ResponseType, } from "@octopusdeploy/octopus-server-client";
import type { QueryParamValuesSetter } from "@octopusdeploy/portal-routes";
import { links } from "@octopusdeploy/portal-routes";
import { noOp } from "@octopusdeploy/utilities";
import { isEqual, keyBy } from "lodash";
import pluralize from "pluralize";
import * as React from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import type { ProcessTemplateCommittedEvent } from "~/analytics/Analytics";
import { Action, useAnalyticProcessTemplateProducerActionDispatch, useAnalyticTrackedProcessTemplateProducerActionDispatch } from "~/analytics/Analytics";
import { lastAccessedPlatformHubGitRef } from "~/areas/platformHub/LastAccessedPlatformHubGitRef";
import { PlatformHubPageHeaderBranchSelector } from "~/areas/platformHub/PlatformHubPageHeaderBranchSelector";
import { applyCommonLogicToProducerProcessResource } from "~/areas/platformHub/PlatformHubProducerProcessHelpers";
import { PlatformHubVcsError } from "~/areas/platformHub/PlatformHubVcsError";
import { EditProcessTemplateSettingsTab } from "~/areas/platformHub/processTemplates/EditProcessTemplateSettingsTab";
import { ProcessTemplateParametersTab } from "~/areas/platformHub/processTemplates/ProcessTemplateParametersTab";
import type { CommitType, WizardSaveParams } from "~/areas/platformHub/processTemplates/ProcessTemplateWizardDialog";
import { ActionCompleted, ProcessTemplateWizardDialog, TemplateWizardSteps } from "~/areas/platformHub/processTemplates/ProcessTemplateWizardDialog";
import type { SharedSpaces } from "~/areas/platformHub/processTemplates/ShareTemplateDialog";
import { ShareTemplateSpacesType } from "~/areas/platformHub/processTemplates/ShareTemplateDialog";
import { StopShareTemplateDialog } from "~/areas/platformHub/processTemplates/StopSharingTemplateDialog";
import { useRequiredPlatformHubGitRef } from "~/areas/platformHub/usePlatformHubGitRef";
import type { AddStepTemplateResult } from "~/areas/projects/components/Process/Common/CommonProcessHelpers";
import { useActionTemplatesFromContext } from "~/areas/projects/components/Process/Contexts/ProcessActionTemplatesContextProvider";
import { useProcessContext } from "~/areas/projects/components/Process/Contexts/ProcessContext";
import type { ProcessStateSelectors } from "~/areas/projects/components/Process/Contexts/ProcessContextState";
import { ProcessController } from "~/areas/projects/components/Process/Contexts/ProcessController";
import { useProcessErrorActions, useProcessErrorSelectors } from "~/areas/projects/components/Process/Contexts/ProcessErrors/ProcessErrorsContext";
import { createDefaultFilter, ProcessQueryStringController, useProcessQueryStringContext } from "~/areas/projects/components/Process/Contexts/ProcessQueryString/ProcessQueryStringContext";
import { useProcessSearchFilterContext } from "~/areas/projects/components/Process/Contexts/ProcessSearchFilter/ProcessSearchFilterContext";
import type { BoundWarningActionsType } from "~/areas/projects/components/Process/Contexts/ProcessWarnings/ProcessWarningsContext";
import { useProcessWarningActions } from "~/areas/projects/components/Process/Contexts/ProcessWarnings/ProcessWarningsContext";
import { ProcessListAdvancedFilters } from "~/areas/projects/components/Process/CustomPaperLayouts/ProcessContextPaperLayout";
import ProcessList from "~/areas/projects/components/Process/ProcessList";
import ProcessStepsLayout, { intentFromFilter, ProcessPageIntent } from "~/areas/projects/components/Process/ProcessStepsLayout";
import type { ProcessStepsLayoutLoaderLookupData } from "~/areas/projects/components/Process/ProcessStepsLayoutLoader";
import { emptyVariableSetResource } from "~/areas/projects/components/Process/ProcessStepsLayoutLoader";
import type { ProducerProcessTemplateContextProps } from "~/areas/projects/components/Process/ProcessTemplates/ProducerProcessTemplateContext";
import { buildProducerProcessTemplateContextProps, ProducerProcessTemplateContext } from "~/areas/projects/components/Process/ProcessTemplates/ProducerProcessTemplateContext";
import type { ProcessTemplateProcessIdentifier } from "~/areas/projects/components/Process/types";
import { isProcessTemplateProcessIdentifier, toProcessTemplateProcessIdentifier } from "~/areas/projects/components/Process/types";
import { GitRefChip } from "~/areas/projects/components/Releases/GitRefChip/GitRefChip";
import { ContextAddStepButton } from "~/areas/projects/components/Steps/index";
import type { CommitMessageWithDetails } from "~/areas/projects/components/VersionControl/CommitMessageWithDetails";
import { getFormattedCommitMessage } from "~/areas/projects/components/VersionControl/CommitMessageWithDetails";
import { PersistenceSettingsContextProvider } from "~/areas/projects/context/PersistenceSettingsContext";
import { repository } from "~/clientInstance";
import Chip from "~/components/Chips/Chip";
import { getErrorsFromError } from "~/components/DataBaseComponent/Errors";
import type { DoBusyTask } from "~/components/DataBaseComponent/index";
import { useDoBusyTaskEffect } from "~/components/DataBaseComponent/index";
import { useLegacyDoBusyTask } from "~/components/DataBaseComponent/useLegacyDoBusyTask";
import { useDialogTrigger } from "~/components/Dialog/DialogTrigger";
import FilterSearchBox from "~/components/FilterSearchBox/index";
import { LegacyForm } from "~/components/FormPaperLayout/LegacyForm";
import OnboardingPage from "~/components/GettingStarted/OnboardingPage";
import { useSpaceAwareNavigation } from "~/components/Navigation/SpaceAwareNavigation/useSpaceAwareNavigation";
import { PageContent } from "~/components/PageContent/PageContent";
import { hasPermission, isAllowed } from "~/components/PermissionCheck/PermissionCheck";
import { PermissionCheck } from "~/components/PermissionCheck/index";
import convertPropertyValueResourceToString from "~/components/convertPropertyValueResourceToString";
import * as tagSets from "~/components/tagSets";
import useLocalStorage from "~/hooks/useLocalStorage";
import type { GlobalState } from "~/store/reducers";
import DateFormatter from "~/utils/DateFormatter/index";
import { createSnackbarMessage, GetCallout } from "./TemplateParametersTable";
interface EditProcessTemplateProps {
    loaderData: LoaderData;
    slug: string;
    queryParams: EditProcessTemplateQueryParams;
    setQueryParams: QueryParamValuesSetter<EditProcessTemplateQueryParams>;
    scrollAreaRef: React.RefObject<HTMLDivElement>;
}
type EditProcessTemplateActiveTab = "process" | "parameters" | "settings" | "versions";
export interface EditProcessTemplateQueryParams {
    gitRef: string;
    activeTab: EditProcessTemplateActiveTab;
}
interface EditWithAllContextProps extends EditProcessTemplateProps {
    loaderData: DataWithProcessTemplate;
    doBusyTask: DoBusyTask;
    errors: Error[];
    busy: boolean;
    gitRef: GitRef;
    setGitRef: (gitRef: string) => void;
    validatedGitRef: ValidateGitRefV2Response | undefined;
}
interface EditProcessTemplateInternalProps {
    cleanModel: ProcessTemplateResource;
    model: ProcessTemplateResource;
    doBusyTask: DoBusyTask;
    errors: Error[];
    busy: boolean;
    lookupData: EditProcessTemplateLookupData;
    icons: IconSvgResource[];
    iconMetadata: IconMetadataResource | undefined;
    onChange: (processTemplate: ProcessTemplateResource) => void;
    gitRefResource: GitRefResource | undefined;
    gitRef: GitRef;
    setGitRef: (gitRef: string) => void;
    gitPersistenceSettings: PlatformHubVersionControlSettingsResource;
    activeTab: EditProcessTemplateActiveTab;
    setActiveTab: (value: EditProcessTemplateActiveTab) => void;
    scrollAreaRef: React.RefObject<HTMLDivElement>;
}
export interface EditProcessTemplateLookupData extends ProcessStepsLayoutLoaderLookupData {
    spaces: SpaceResource[];
}
function EditWithAllContext({ loaderData, slug, doBusyTask, busy, errors, queryParams, setQueryParams, scrollAreaRef, gitRef, setGitRef, validatedGitRef }: EditWithAllContextProps) {
    const [lookupData, setLookupData] = useState<EditProcessTemplateLookupData>({ ...defaultLookups, templates: loaderData.processTemplate.Parameters });
    const [icons, setIcons] = useState<IconSvgResource[]>([]);
    const [iconMetadata, setIconMetadata] = useState<IconMetadataResource>();
    const { result: cleanModel, refetch: reloadProcessTemplate } = useQuery(async (repository) => {
        const result = await repository.ProcessTemplates.get(slug, gitRef);
        setModel(result);
        return result;
    }, [slug, gitRef], "Get process template", {
        initialResult: loaderData.processTemplate,
    });
    const [model, setModel] = useState<ProcessTemplateResource>(cleanModel);
    const [producerProcessTemplateContextProps, setProducerProcessTemplateContextProps] = useState<ProducerProcessTemplateContextProps>(buildProducerProcessTemplateContextProps(cleanModel));
    const refreshLookups = useCallback(async () => {
        const environments = await repository.Environments.all();
        const tenantTagSets = tagSets.getAllByScopes(["Tenant"]);
        setLookupData({
            ...defaultLookups,
            environmentsById: keyBy(environments, "Id"),
            environmentsV2ById: keyBy(environments.map(mapFromEnvironmentResourceToEnvironmentV2Resource), "Id"),
            tagSets: await tenantTagSets,
            machineRoles: await repository.MachineRoles.all(),
            tagIndex: await tagSets.getTagIndex(["Tenant"]),
            spaces: await repository.Spaces.all(),
        });
    }, []);
    const onChange = useCallback((processTemplate: ProcessTemplateResource) => {
        const context = buildProducerProcessTemplateContextProps(processTemplate);
        setProducerProcessTemplateContextProps(context);
        setLookupData({ ...lookupData, templates: processTemplate.Parameters });
        setModel(processTemplate);
    }, [lookupData]);
    useDoBusyTaskEffect(doBusyTask, async () => {
        await refreshLookups();
        const icons = await repository.Icons.getIcons();
        setIcons(icons);
        const iconMetadata = await repository.Icons.getIconMetadata();
        setIconMetadata(iconMetadata);
    }, [refreshLookups]);
    const processIdentifier: ProcessTemplateProcessIdentifier = useMemo(() => toProcessTemplateProcessIdentifier(slug, gitRef), [slug, gitRef]);
    const setActiveTab = useCallback((value: EditProcessTemplateActiveTab) => setQueryParams((prev) => ({ ...prev, activeTab: value })), [setQueryParams]);
    return (<ProducerProcessTemplateContext.Provider value={producerProcessTemplateContextProps}>
            <PersistenceSettingsContextProvider {...loaderData.persistenceSettings}>
                <ProcessController layoutActions={{ refreshLookupData: refreshLookups }} doBusyTask={doBusyTask} processIdentifier={processIdentifier} process={cleanModel} reloadProcess={reloadProcessTemplate} // Only used when updating step templates which process templates can't do yet, so this is actually unused
     modifyProcess={() => Promise.resolve()} // This function is never used for process templates since the save logic is handled outside the process context
    >
                    {() => {
            return (<ProcessQueryStringController initialQueryFilter={createDefaultFilter()}>
                                {() => {
                    return (<EditProcessTemplatePageInternal cleanModel={cleanModel} model={model} doBusyTask={doBusyTask} busy={busy} errors={errors} lookupData={lookupData} gitRefResource={validatedGitRef?.GitRef} gitRef={gitRef} setGitRef={setGitRef} gitPersistenceSettings={loaderData.persistenceSettings} icons={icons} iconMetadata={iconMetadata} onChange={onChange} activeTab={queryParams.activeTab} setActiveTab={setActiveTab} scrollAreaRef={scrollAreaRef}/>);
                }}
                            </ProcessQueryStringController>);
        }}
                </ProcessController>
            </PersistenceSettingsContextProvider>
        </ProducerProcessTemplateContext.Provider>);
}
const defaultLookups: EditProcessTemplateLookupData = {
    includedScriptModules: [],
    lifecyclePreview: null,
    environmentsById: {},
    environmentsV2ById: {},
    channelsById: null,
    tagSets: [],
    workerPoolsById: {},
    machineRoles: [],
    tagIndex: {},
    userOnboarding: null,
    workerPoolsSummary: {
        WorkerPoolSummaries: [],
        TotalMachines: 0,
        TotalDisabledMachines: 0,
        MachineHealthStatusSummaries: {
            Healthy: 0,
            Unavailable: 0,
            Unknown: 0,
            HasWarnings: 0,
            Unhealthy: 0,
        },
        MachineEndpointSummaries: {
            None: 0,
            TentaclePassive: 0,
            TentacleActive: 0,
            Ssh: 0,
            OfflineDrop: 0,
            AzureWebApp: 0,
            AzureCloudService: 0,
            AzureServiceFabricCluster: 0,
            Kubernetes: 0,
            KubernetesTentacle: 0,
            StepPackage: 0,
        },
        DeploymentTargetSummaries: {
            Healthy: 0,
            Unavailable: 0,
            Unknown: 0,
            HasWarnings: 0,
            Unhealthy: 0,
        },
        TentacleUpgradesRequired: false,
        MachineIdsForCalamariUpgrade: [],
        MachineIdsForTentacleUpgrade: [],
    },
    projectTriggers: [],
    processVariableSet: emptyVariableSetResource(""), // Empty spaceId while variable sets are space-scoped
    libraryVariableSets: [],
    templates: [],
    spaces: [],
    teams: [],
    projects: [],
};
interface ProcessOverviewContentProps {
    lookupData: ProcessStepsLayoutLoaderLookupData;
    doBusyTask: DoBusyTask;
    gitRefResource: GitRefResource | undefined;
    showProcessOverviewStepNotes: boolean;
}
function ProcessOverviewContent({ lookupData, doBusyTask, gitRefResource, showProcessOverviewStepNotes }: ProcessOverviewContentProps) {
    const location = useLocation();
    return <ProcessList lookups={lookupData} doBusyTask={doBusyTask} location={location} gitRefResource={gitRefResource} showStepNotes={showProcessOverviewStepNotes}/>;
}
interface ProcessEditorContentProps {
    lookupData: ProcessStepsLayoutLoaderLookupData;
    doBusyTask: DoBusyTask;
    errors: Error[];
    busy: boolean;
    gitRefResource: GitRefResource | undefined;
    scrollAreaRef: React.RefObject<HTMLDivElement>;
    onCustomStepTemplateAdded: (stepTemplate: ActionTemplateResource, result: AddStepTemplateResult) => void;
    onParameterAdded: (parameters: ProcessTemplateParameterResource[]) => void;
}
function ProcessEditorContent({ lookupData, busy, doBusyTask, errors, gitRefResource, scrollAreaRef, onCustomStepTemplateAdded, onParameterAdded }: ProcessEditorContentProps) {
    const isBuiltInWorkerEnabled = useSelector((state: GlobalState) => state.configurationArea.features.isBuiltInWorkerEnabled);
    return (<ProcessStepsLayout gitRef={gitRefResource?.CanonicalName} titleAccessory={undefined} stepSlug={undefined} setShowK8sStatusItem={undefined} onProcessSave={() => Promise.resolve()} changeGitRef={noOp} // todo
     refreshModel={() => Promise.resolve(true)} includedLibraryVariableSetIds={[]} releaseCreationPackageStepId={undefined} lookups={lookupData} errors={errors} busy={busy} doBusyTask={doBusyTask} isBuiltInWorkerEnabled={isBuiltInWorkerEnabled} gitRefResource={gitRefResource} isTenanted={true} onValidationError={() => scrollAreaRef.current?.scrollTo({ top: 0, behavior: "smooth" })} onCustomStepTemplateAdded={onCustomStepTemplateAdded} onParameterAdded={onParameterAdded}/>);
}
interface QuickCommitParams {
    type: "Quick Commit";
}
export type SaveProcessTemplateParams = QuickCommitParams | WizardSaveParams;
type ImportStepTemplateResult = {
    stepTemplate: ActionTemplateResource;
    addedParameters: ProcessTemplateParameterResource[];
    duplicateParameters: ProcessTemplateParameterResource[];
    addResult: AddStepTemplateResult;
};
function EditProcessTemplatePageInternal({ cleanModel, model, doBusyTask, busy, errors, lookupData, icons, iconMetadata, onChange, gitRefResource, gitRef, setGitRef, gitPersistenceSettings, activeTab, setActiveTab, scrollAreaRef, }: EditProcessTemplateInternalProps) {
    const process = useProcessContext();
    const processIdentifier = process.state.processIdentifier;
    const hasValidProcess = process.selectors.hasValidProcess();
    const hasSteps = process.selectors.hasSteps();
    const errorActions = useProcessErrorActions();
    const warningActions = useProcessWarningActions();
    const errorSelectors = useProcessErrorSelectors();
    const navigation = useSpaceAwareNavigation();
    const [disableDirtyFormChecking, setDisableDirtyFormChecking] = useState<boolean>(false);
    const showSnackbar = useShowSnackbar();
    const { openDialog: openCommitPublishShareDialog, isOpen: isCommitPublishShareDialogOpen, closeAllDialogs: closeCommitPublishShareDialog } = useDialogTrigger();
    const { openDialog: openStopShareDialog, isOpen: isStopShareDialogOpen } = useDialogTrigger();
    const [dismissedShareAndPublishCallout, setDismissed] = useLocalStorage(`Octopus.Callout.ProcessTemplate.ShareAndPublish.${cleanModel.Slug}.Dismissed`, false);
    const [wizardSteps, setWizardSteps] = useState<TemplateWizardSteps[]>([]);
    const [showInitialSuccessCallout, setShowInitialSuccessCallout] = useState<boolean>(false);
    const processSearchFilterContext = useProcessSearchFilterContext();
    const processQueryStringContext = useProcessQueryStringContext();
    const { state: processSearchFilterContextLookupsState, actions: processSearchFilterContextActions } = processSearchFilterContext;
    const [showProcessOverviewStepNotes, setShowProcessOverviewStepNotes] = useState(true);
    const intent = intentFromFilter(processQueryStringContext.state.queryFilter);
    const [latestImportedStepTemplate, setLatestImportedStepTemplate] = useState<ImportStepTemplateResult | null>(null);
    const actionTemplates = useActionTemplatesFromContext();
    const dispatchProcessTemplateAction = useAnalyticProcessTemplateProducerActionDispatch(model.Slug, IsDefaultBranch(gitPersistenceSettings, gitRef) ?? false, isProtectedBranch(gitRefResource));
    const trackProcessTemplateAction = useAnalyticTrackedProcessTemplateProducerActionDispatch(model.Slug, IsDefaultBranch(gitPersistenceSettings, gitRef) ?? false, isProtectedBranch(gitRefResource));
    const { result: publishedVersions, refetch: reloadPublishedVersions } = useQuery((repository) => repository.ProcessTemplates.getVersions(cleanModel.Slug), [cleanModel.Slug], "Get process template versions");
    const { result: sharingConfiguration, refetch: reloadSharingConfiguration } = useQuery((repository) => repository.ProcessTemplates.getSharingConfiguration(model.Slug), [model.Slug], "Get spaces process template is shared to");
    const { result: processTemplateLink } = useQuery((repository) => repository.ProcessTemplates.getLink(model.Slug, model.GitRef), [model.Slug, model.GitRef], "Get process template link");
    const isTemplateShared = sharingConfiguration?.SharedToAllSpaces || (sharingConfiguration && sharingConfiguration.IndividuallySharedSpaceIds.length > 0);
    const { execute: handleStopShare } = useMutation({
        name: "Stop sharing process template",
        action: async (repository: Repository, sharedSpaces: SharedSpaces) => {
            const shareCommand: ShareProcessTemplateCommand = {
                ProcessTemplateSlug: model.Slug,
                GitRef: model.GitRef,
                ShareToAllSpaces: sharedSpaces.shareToAll,
                IndividuallySharedSpaceIds: sharedSpaces.individualSpacesSharedTo,
            };
            return await repository.ProcessTemplates.share(shareCommand);
        },
        afterComplete: async (repository, output) => {
            dispatchProcessTemplateAction("Process Template Unshared", { type: "Process Template Unshared", action: Action.Share, sharedTo: ShareTemplateSpacesType.NotShared });
            reloadSharingConfiguration();
            const snackbarMessage = "Process template has been unshared from all spaces.";
            showSnackbar(snackbarMessage);
        },
    });
    const { execute: saveProcessTemplate } = useMutation({
        name: "Save process template",
        action: async (repository: Repository, params: SaveProcessTemplateParams) => {
            if (params.type === "Quick Commit") {
                await saveProcess(false, "Quick Commit");
                return [ActionCompleted.Committed];
            }
            else {
                const { commitParams, publishParams, shareParams } = params;
                const actionsCompleted: ActionCompleted[] = [];
                if (commitParams) {
                    const isCommitOnly = !!commitParams && !publishParams && !shareParams;
                    await saveProcess(false, isCommitOnly ? "Commit Only" : "Commit and Publish", commitParams.commitMessage);
                    actionsCompleted.push(ActionCompleted.Committed);
                }
                if (publishParams) {
                    const { versionToPublish, isPreRelease, releaseType } = publishParams;
                    await publishProcessTemplate(repository, versionToPublish, isPreRelease);
                    actionsCompleted.push(ActionCompleted.Published);
                    dispatchProcessTemplateAction("Process Template Published", {
                        type: "Process Template Published",
                        action: Action.Add,
                        releaseType,
                        isPreRelease,
                    });
                }
                if (shareParams) {
                    const { shareType, selectedSpaces } = shareParams;
                    const shareToAll = shareType === ShareTemplateSpacesType.ShareAll;
                    const sharedToIndividual = shareType === ShareTemplateSpacesType.ShareIndividual;
                    await shareProcessTemplate(repository, {
                        shareToAll,
                        individualSpacesSharedTo: sharedToIndividual ? selectedSpaces : [],
                    });
                    const spaceCount = sharedToIndividual ? selectedSpaces.length : undefined;
                    actionsCompleted.push(ActionCompleted.Shared);
                    dispatchProcessTemplateAction("Process Template Shared", {
                        type: "Process Template Shared",
                        action: Action.Share,
                        sharedTo: shareType,
                        spaceCount,
                    });
                }
                return actionsCompleted;
            }
        },
        afterComplete: async (repository, result) => {
            errorActions.clearErrors();
            warningActions.clearWarnings();
            if (result.includes(ActionCompleted.Committed) && result.includes(ActionCompleted.Published) && result.includes(ActionCompleted.Shared)) {
                setShowInitialSuccessCallout(true);
            }
            const snackbarMessage = createSnackbarMessage(result, "process template");
            if (snackbarMessage) {
                showSnackbar(snackbarMessage);
            }
            reloadDataAfterSave();
        },
        onError: (error) => {
            errorActions.setErrors(getErrorsFromError(error), process.selectors);
            warningActions.clearWarnings();
        },
    });
    const publishProcessTemplate = async (repository: Repository, version: string, isPreRelease: boolean) => {
        const publishVersion: CreateProcessTemplateVersionCommand = {
            ProcessTemplateSlug: model.Slug,
            GitRef: model.GitRef,
            Version: version,
            IsPreRelease: isPreRelease,
        };
        return await repository.ProcessTemplates.createVersion(publishVersion);
    };
    const shareProcessTemplate = async (repository: Repository, sharedSpaces: SharedSpaces) => {
        const shareCommand: ShareProcessTemplateCommand = {
            ProcessTemplateSlug: model.Slug,
            GitRef: model.GitRef,
            ShareToAllSpaces: sharedSpaces.shareToAll,
            IndividuallySharedSpaceIds: sharedSpaces.individualSpacesSharedTo,
        };
        return await repository.ProcessTemplates.share(shareCommand);
    };
    const saveProcess = async (isNavigationConfirmation: boolean, commitType?: CommitType, commitMessage?: CommitMessageWithDetails) => {
        if (isNavigationConfirmation) {
            setWizardSteps([TemplateWizardSteps.Commit]);
            openCommitPublishShareDialog();
        }
        else {
            const { modelProcess: clientProcessResource } = process.selectors.getProcessesForMerge();
            applyCommonLogicToProducerProcessResource(clientProcessResource, lookupData.workerPoolsSummary, process.selectors.getActionPlugin, ProcessType.ProcessTemplate);
            const updatedProcessTemplate: ModifyProcessTemplateCommand = {
                ...model,
                Steps: clientProcessResource.Steps,
                ChangeDescription: getFormattedCommitMessage(commitMessage ?? { summary: "", details: "" }, getDefaultCommitMessage()),
            };
            const toTemplate = (a: DeploymentActionResource) => actionTemplates.find((t) => t.Type === a.ActionType);
            const ev: ProcessTemplateCommittedEvent = {
                type: "Process Template Committed",
                action: Action.Commit,
                stepTemplate: updatedProcessTemplate.Steps.flatMap((s) => s.Actions)
                    .map((a) => toTemplate(a)?.Name)
                    .filter((name): name is string => name !== undefined),
                parameters: updatedProcessTemplate.Parameters.map((p) => p.DisplaySettings["Octopus.ControlType"] ?? ControlType.SingleLineText),
                commitMessage: commitMessage ? commitMessage.summary.length > 0 : false,
                commitType: commitType ?? "Quick Commit", // The only time commit type won't exist is if we're navigating away from the page, in which case this fallback shouldn't be reached.
            };
            await trackProcessTemplateAction("Process Template Committed", ev, async () => await repository.ProcessTemplates.modify(updatedProcessTemplate));
            process.actions.refreshFromServer();
            if (!hasSteps) {
                if (!isProcessTemplateProcessIdentifier(processIdentifier)) {
                    throw new Error("This page only supports process templates");
                }
                const gitRef = model.GitRef;
                navigation.navigate(links.editProcessTemplatePage.generateUrl({ slug: processIdentifier.slug }, { gitRef }));
            }
        }
    };
    const onCustomStepTemplateAdded = useCallback((stepTemplate: ActionTemplateResource, result: AddStepTemplateResult) => {
        const newProcessTemplateParameters = convertActionTemplateParametersToProcessTemplateParameters(stepTemplate.Parameters);
        const existingParameterNames = new Set(model.Parameters.map((p) => p.Name));
        const uniqueNewParameters = newProcessTemplateParameters.filter((p) => !existingParameterNames.has(p.Name));
        if (uniqueNewParameters.length > 0) {
            const updatedModel = {
                ...model,
                Parameters: [...model.Parameters, ...uniqueNewParameters],
            };
            onChange(updatedModel);
        }
        setLatestImportedStepTemplate({ stepTemplate, addedParameters: uniqueNewParameters, duplicateParameters: newProcessTemplateParameters.filter((p) => existingParameterNames.has(p.Name)), addResult: result });
    }, [model, onChange]);
    const reloadDataAfterSave = () => {
        reloadPublishedVersions();
        reloadSharingConfiguration();
    };
    const publishButton: PageAction = {
        key: "PublishButton",
        type: "custom",
        content: (<PermissionCheck permission={Permission.PlatformHubEdit}>
                <ActionButton label={"Publish"} type={ActionButtonType.Secondary} accessibleName={"Publish"} onClick={() => {
                setWizardSteps([TemplateWizardSteps.Publish]);
                openCommitPublishShareDialog();
            }}/>
            </PermissionCheck>),
    };
    const commitButton: PageAction = {
        key: "CommitButton",
        type: "custom",
        content: (<PermissionCheck permission={Permission.PlatformHubEdit}>
                <div role="group" className={styles.commitButtonContainerStyles}>
                    <ActionButton label={"Commit"} type={ActionButtonType.Primary} accessibleName={"Commit"} onClick={() => {
                if (isTemplateShared) {
                    setWizardSteps([TemplateWizardSteps.Commit, TemplateWizardSteps.Publish]);
                }
                else {
                    setWizardSteps([TemplateWizardSteps.Commit, TemplateWizardSteps.Publish, TemplateWizardSteps.Share]);
                }
                openCommitPublishShareDialog();
            }} disabled={disableDirtyFormChecking ? false : isEqual(model, cleanModel) || busy || !hasPermission(Permission.PlatformHubEdit)}/>
                    <Button importance="primary" icon={<CodeCommitIcon size={20}/>} accessibleName={"Quick commit"} disabled={disableDirtyFormChecking ? false : isEqual(model, cleanModel) || busy || !hasPermission(Permission.PlatformHubEdit)} onClick={() => saveProcessTemplate({ type: "Quick Commit" })}/>
                </div>
            </PermissionCheck>),
    };
    const onParametersChanged = useCallback((parameters: ActionTemplateParameterResource[]) => {
        const resource: ProcessTemplateResource = { ...model, Parameters: parameters.map((p) => (isProcessTemplateParameterResource(p) ? p : { ...p, Values: [] })) };
        onChange(resource);
    }, [onChange, model]);
    let secondaryActions: PageAction[] = [addStepButton, publishButton];
    if (activeTab === "process") {
        secondaryActions = [addStepButton, publishButton];
    }
    else if (activeTab === "settings") {
        secondaryActions = [publishButton];
    }
    else if (activeTab === "parameters") {
        secondaryActions = [publishButton];
    }
    const onSettingsChange = useCallback((settings: ProcessTemplateResource) => {
        const resource: ProcessTemplateResource = { ...model, ...settings };
        onChange(resource);
    }, [onChange, model]);
    const processFromContext = useMemo(() => (process.state.model.process ? process.selectors.getProcessResource() : null), [process.selectors, process.state.model.process]);
    useEffect(() => {
        if (!processFromContext) {
            return;
        }
        setUnsupportedStepWarnings(processFromContext.Steps, warningActions, process.selectors);
    }, [process.selectors, processFromContext, warningActions]);
    useEffect(() => {
        if (processFromContext && !isEqual(model.Steps, processFromContext.Steps)) {
            const resource: ProcessTemplateResource = { ...model, Steps: processFromContext.Steps };
            onChange(resource);
        }
    }, [processFromContext, onChange, model]);
    function getDefaultCommitMessage(): string {
        return "Update process template";
    }
    const overflowActions: SimpleMenuItem[] = [];
    if (isAllowed({ permission: Permission.PlatformHubEdit })) {
        overflowActions.push({
            type: "button",
            label: "Share",
            description: isTemplateShared ? undefined : "Not shared to any spaces",
            onClick: () => {
                setWizardSteps([TemplateWizardSteps.Share]);
                openCommitPublishShareDialog();
            },
        });
        if (processTemplateLink?.Link) {
            overflowActions.push({
                type: "external-link",
                label: "View source code",
                href: processTemplateLink.Link,
            });
        }
        overflowActions.push({
            type: "divider",
        });
        if (isTemplateShared) {
            overflowActions.push({
                type: "button",
                isDestructive: true,
                label: "Stop sharing this template",
                onClick: openStopShareDialog,
            });
        }
        else {
            overflowActions.push({
                type: "disabled",
                label: "Stop sharing this template",
            });
        }
    }
    const titleAccessory = <PlatformHubPageHeaderBranchSelector setGitRef={setGitRef} gitRef={gitRef} persistenceSettings={gitPersistenceSettings}/>;
    const shouldShowShareChip = sharingConfiguration === null ? false : hasPermission(Permission.PlatformHubEdit) && cleanModel.Steps.length > 0 && !isTemplateShared;
    const shouldShowShareAndPublishCallout = sharingConfiguration === null ? false : hasPermission(Permission.PlatformHubEdit) && !dismissedShareAndPublishCallout && cleanModel.Steps.length > 0 && publishedVersions?.length === 0 && !isTemplateShared;
    const anyProcessSpecificErrors = errorSelectors.getActionsWithErrors().length > 0 || errorSelectors.getStepsWithErrors().length > 0;
    const anyParametersSpecificErrors = errors.some((e) => (OctopusError.isOctopusError(e) && e.Details ? Object.keys(e.Details).some((k) => k.startsWith("Parameter")) : false));
    // The only known settings-specific error is if no name is provided.
    const anySettingsSpecificErrors = errors.some((e) => (OctopusError.isOctopusError(e) && e.Details ? Object.keys(e.Details).some((k) => k === "Name") : false));
    const tabWarningMessage = "Errors found in this section";
    const processFilters = hasValidProcess && hasSteps
        ? {
            inputs: [
                <FilterSearchBox placeholder="Filter by name..." value={processSearchFilterContextLookupsState.searchFilter.filterKeyword} onChange={(filterKeyword) => processSearchFilterContextActions.onFilterChange((prev) => ({ ...prev, filterKeyword }))} autoFocus={true}/>,
                <Checkbox label={"Show notes"} value={showProcessOverviewStepNotes} onChange={setShowProcessOverviewStepNotes} noMargin={true}/>,
            ],
            advancedFilters: {
                content: <ProcessListAdvancedFilters processType={ProcessType.ProcessTemplate} environmentLookupData={lookupData.environmentsById}/>,
                onResetFilter: () => processSearchFilterContextActions.onFilterChange(() => processSearchFilterContext.getEmptyFilter()),
                hasUserSelectedValues: processSearchFilterContext.isFiltering,
            },
        }
        : undefined;
    const callout = GetCallout({
        showInitialSuccessCallout,
        setShowInitialSuccessCallout,
        shouldShowShareAndPublishCallout,
        modelName: model.Name,
        onPublishShareClick: () => {
            setWizardSteps([TemplateWizardSteps.Publish, TemplateWizardSteps.Share]);
            openCommitPublishShareDialog();
        },
        onDismissShareAndPublishCallout: () => setDismissed(true),
    });
    return (<>
            <header className={styles.pageHeaderContainer}>
                <div className={styles.breadcrumbContainer}>{<Breadcrumbs items={[{ label: "Process Templates", pageUrl: links.processTemplatesPage.generateUrl() }]} showBackIcon={true}/>}</div>
                <div className={styles.titleContainer}>
                    <Avatar type="template" size="medium" src={repository.Icons.getIconUrl(model.Icon)} alt="Process template icon"/>
                    <h1 title={model.Name ?? "Edit process template"} className={styles.title}>
                        {model.Name ?? "Edit process template"}
                    </h1>
                    {shouldShowShareChip ? <Chip backgroundColor={themeTokens.color.badge.background.neutral}>Not shared</Chip> : undefined}
                </div>
            </header>

            <LegacyForm devToolsDirtyTrackingKey="Process Template" disableDirtyFormChecking={disableDirtyFormChecking} onSaveClick={saveProcess} savePermission={{ permission: Permission.PlatformHubEdit }} model={model} cleanModel={cleanModel} forceDisableFormSaveButton={busy}>
                {({ FormContent }) => (<PageContent busy={busy} header={{
                title: "Process Editor",
                primaryAction: commitButton,
                pageActions: secondaryActions,
                contextSelector: titleAccessory,
                overflowActions: overflowActions,
            }} callout={callout} currentTab={activeTab} onTabChanged={setActiveTab} tabs={[
                {
                    label: "Process",
                    value: "process",
                    warning: anyProcessSpecificErrors ? tabWarningMessage : undefined,
                    content: intent === ProcessPageIntent.Unknown ? (<ProcessOverviewContent lookupData={lookupData} doBusyTask={doBusyTask} gitRefResource={gitRefResource} showProcessOverviewStepNotes={showProcessOverviewStepNotes}/>) : (<>
                                            {latestImportedStepTemplate ? <StepTemplateImportedCallout onClose={() => setLatestImportedStepTemplate(null)} latestImportedStepTemplate={latestImportedStepTemplate}/> : null}
                                            <ProcessEditorContent lookupData={lookupData} errors={errors} busy={busy} doBusyTask={doBusyTask} gitRefResource={gitRefResource} scrollAreaRef={scrollAreaRef} onCustomStepTemplateAdded={onCustomStepTemplateAdded} onParameterAdded={onParametersChanged}/>
                                        </>),
                    filters: intent === ProcessPageIntent.Unknown ? processFilters : undefined,
                },
                {
                    label: "Parameters",
                    value: "parameters",
                    warning: anyParametersSpecificErrors ? tabWarningMessage : undefined,
                    content: <ProcessTemplateParametersTab parameters={model.Parameters} onChange={onParametersChanged} dispatchProcessTemplateAction={dispatchProcessTemplateAction} processTemplateLink={processTemplateLink?.Link}/>,
                },
                {
                    label: "Settings",
                    value: "settings",
                    warning: anySettingsSpecificErrors ? tabWarningMessage : undefined,
                    content: (<FormContent hideExpandAll={false}>
                                        <EditProcessTemplateSettingsTab processTemplate={model} onChange={onSettingsChange} iconSvgResources={icons} iconMetadata={iconMetadata}/>
                                    </FormContent>),
                },
                {
                    label: "Versions",
                    value: "versions",
                    content: <ProcessTemplateVersions publishedVersions={publishedVersions}/>,
                },
            ]}/>)}
            </LegacyForm>

            {publishedVersions ? (<ProcessTemplateWizardDialog open={isCommitPublishShareDialogOpen} busy={busy} errors={errors} model={model} gitRef={gitRef} reloadDataAfterSave={reloadDataAfterSave} lookupData={lookupData} sharingConfiguration={sharingConfiguration} publishedVersions={publishedVersions} stepsToShow={wizardSteps} getDefaultCommitMessage={getDefaultCommitMessage} saveProcessTemplate={saveProcessTemplate} setShowInitialSuccessCallout={setShowInitialSuccessCallout} closeDialog={closeCommitPublishShareDialog} key={isCommitPublishShareDialogOpen ? "dialog-open" : "dialog-closed"} // This ensures the state is always new when the dialog is opened
         dispatchProcessTemplateAction={dispatchProcessTemplateAction}/>) : null}

            <Dialog open={isStopShareDialogOpen}>{<StopShareTemplateDialog onSave={handleStopShare} onClose={openStopShareDialog}/>}</Dialog>
        </>);
}
function ProcessTemplateVersions({ publishedVersions }: {
    publishedVersions: ProcessTemplateVersionResource[] | null;
}) {
    if (publishedVersions === null) {
        return null;
    }
    if (publishedVersions.length === 0) {
        return (<OnboardingPage title="No versions found for this template" intro="Create versions of process templates on any branch to test and publish to the rest of your team. To get started on publishing a version, select “Publish” in the top right corner." learnMore={null}/>);
    }
    return (<div>
            <SimpleDataTable columns={[
            {
                title: "Version",
                render: (data) => (<div className={styles.versionContainer}>
                                <b>{data.Version}</b>
                                {data.IsPreRelease ? <Chip backgroundColor={themeTokens.color.chip.filled.background.info}>Pre-release</Chip> : null}
                            </div>),
                columnSize: "medium",
            },
            { title: "Git reference", render: (data) => <GitRefChip vcsRef={{ GitRef: data.GitRef, GitCommit: data.GitCommit }} showCommit={true}/>, columnSize: "medium" },
            { title: "Published", render: (data) => DateFormatter.dateToLongFormat(data.PublishedDate) },
        ]} data={publishedVersions} getRowKey={(data) => data.Version}/>
        </div>);
}
const styles = {
    versionContainer: css({
        display: "flex",
        alignItems: "center",
        gap: space[6],
    }),
    stopSharingMenuItem: css({
        color: themeTokens.color.text.danger,
    }),
    pageHeaderContainer: css({
        display: "flex",
        flexDirection: "column",
        paddingBottom: space[32],
        gap: "unset",
    }),
    breadcrumbContainer: css({
        display: "flex",
        alignItems: "flex-start",
        height: "1.75rem",
    }),
    titleContainer: css({
        flex: "1",
        display: "flex",
        gap: space[12],
        alignItems: "center",
        overflow: "hidden",
    }),
    title: css({
        all: "unset",
        font: text.heading.large,
        color: themeTokens.color.text.primary,
        overflow: "hidden",
        whiteSpace: "nowrap",
        textOverflow: "ellipsis",
    }),
    commitButtonContainerStyles: css({
        display: "inline-flex",
        gap: space[4],
    }),
    stepTemplateCalloutList: css({
        listStyleType: "disc",
        listStylePosition: "inside",
        margin: 0,
    }),
    unimportedParametersList: css({
        listStyleType: "circle",
        listStylePosition: "inside",
        paddingLeft: space[16],
        margin: 0,
    }),
};
const addStepButton: PageAction = {
    type: "custom",
    content: (<PermissionCheck permission={Permission.PlatformHubEdit}>
            <ContextAddStepButton />
        </PermissionCheck>),
    key: "Add Step",
};
interface StepTemplateImportedCalloutProps {
    onClose: () => void;
    latestImportedStepTemplate: ImportStepTemplateResult;
}
function StepTemplateImportedCallout({ latestImportedStepTemplate: { addedParameters, duplicateParameters, stepTemplate, addResult }, onClose }: StepTemplateImportedCalloutProps) {
    const anyNonTextParametersAdded = addedParameters.some((p) => !isImportableValueControlType(p.DisplaySettings["Octopus.ControlType"]));
    return (<Callout type={"information"} title={`Step template imported successfully`} canClose={true} onClose={onClose}>
            <p>
                The custom step template <strong>{stepTemplate.Name}</strong> was successfully imported and has been detached from the original step template.
            </p>
            <ul className={styles.stepTemplateCalloutList}>
                {addedParameters.length > 0 ? (<>
                        <li>
                            {pluralize("parameter", addedParameters.length, true)} {pluralize("was", addedParameters.length)} imported. Check the Parameters tab to confirm {addedParameters.length > 1 ? "their" : "its"}{" "}
                            {pluralize("name", addedParameters.length)} and {pluralize("value", addedParameters.length)}.
                        </li>
                        {anyNonTextParametersAdded ? <li>Default values for account, certificate, worker pool, and sensitive parameters have been cleared.</li> : null}
                    </>) : null}
                {duplicateParameters.length > 0 ? (<li>
                        The following {pluralize("parameter", duplicateParameters.length)} {pluralize("was", duplicateParameters.length)} not imported as there is already a parameter with the same name:
                        <ul className={styles.unimportedParametersList}>
                            {duplicateParameters.map((p) => (<li key={p.Name}>{p.Name}</li>))}
                        </ul>
                    </li>) : null}
                {addResult.anyAzureAccountsCleared ? <li>Azure account usages have been cleared and will need to be reconfigured.</li> : null}
                {addResult.anyGitCredentialsCleared ? <li>Git credentials have been cleared and will need to be reconfigured.</li> : null}
                {addResult.anyImmediatePackageReferencesCleared ? <li>Selected packages have been cleared and will need to be reconfigured.</li> : null}
                {addResult.anySensitiveValuesCleared ? <li>Sensitive values have been cleared and will need to be reconfigured.</li> : null}
            </ul>
        </Callout>);
}
export async function editProcessTemplateLoader(repository: Repository, gitRef: string, slug: string): Promise<LoaderData> {
    const persistenceSettings = await repository.PlatformHub.getVersionControlSettings();
    if (!gitRef) {
        gitRef = lastAccessedPlatformHubGitRef.get(persistenceSettings);
    }
    const gitRefValidationResult = await repository.PlatformHub.validateGitRef(gitRef);
    if (gitRefValidationResult.Type !== ValidateGitRefV2ResponseType.Success) {
        return {
            persistenceSettings,
            gitRefValidationResult,
        };
    }
    const processTemplate = await repository.ProcessTemplates.get(slug, gitRef);
    return {
        processTemplate,
        persistenceSettings,
        gitRefValidationResult,
    };
}
type LoaderData = DataWithProcessTemplate | DataWithFailedGitRefValidationResult;
type DataWithProcessTemplate = {
    processTemplate: ProcessTemplateResource;
    persistenceSettings: PlatformHubVersionControlSettingsResource;
    gitRefValidationResult: ValidateGitRefV2Response | undefined;
};
type DataWithFailedGitRefValidationResult = {
    gitRefValidationResult: ValidateGitRefV2Response;
    persistenceSettings: PlatformHubVersionControlSettingsResource;
};
export default function EditProcessTemplatePage(props: EditProcessTemplateProps) {
    const { doBusyTask, status: doBusyTaskStatus } = useLegacyDoBusyTask();
    const { isInProgress, errors } = useAggregateAPIOperationStatus(doBusyTaskStatus);
    const [gitRef, setGitRef, gitRefValidationResult] = useRequiredPlatformHubGitRef(props.loaderData.persistenceSettings, props.queryParams, props.setQueryParams, props.loaderData.gitRefValidationResult);
    const loaderData = props.loaderData;
    if (isDataWithInvalidGitRef(loaderData, gitRefValidationResult)) {
        const persistenceSettings = loaderData.persistenceSettings;
        return (<PageContent header={{ title: "Process Editor", contextSelector: <PlatformHubPageHeaderBranchSelector setGitRef={setGitRef} gitRef={gitRef} persistenceSettings={persistenceSettings}/> }}>
                <PlatformHubVcsError error={gitRefValidationResult || loaderData.gitRefValidationResult} gitRef={gitRef} persistenceSettings={persistenceSettings} setGitRef={setGitRef}/>
            </PageContent>);
    }
    return (<EditWithAllContext loaderData={loaderData} slug={props.slug} doBusyTask={doBusyTask} busy={isInProgress} errors={errors} queryParams={props.queryParams} setQueryParams={props.setQueryParams} scrollAreaRef={props.scrollAreaRef} gitRef={gitRef} setGitRef={setGitRef} validatedGitRef={gitRefValidationResult}/>);
}
function isDataWithInvalidGitRef(data: LoaderData, gitRefValidationResult: ValidateGitRefV2Response | undefined): data is DataWithFailedGitRefValidationResult {
    return gitRefValidationResult !== undefined && gitRefValidationResult.Type !== ValidateGitRefV2ResponseType.Success;
}
function setUnsupportedStepWarnings(steps: DeploymentStepResource[], warningActions: BoundWarningActionsType, selectors: ProcessStateSelectors) {
    if (!processTemplateStepsContainAnyUnsupportedActionTypes(steps)) {
        return;
    }
    const warnings: {
        [key: string]: string;
    } = {};
    for (const [i, step] of steps.entries()) {
        for (const [j, action] of step.Actions.entries()) {
            if (unsupportedProcessTemplateActionTypes.includes(action.ActionType)) {
                warnings[`Steps[${i}].Actions[${j}].ActionType`] = "This step is not supported in Process Templates";
            }
        }
    }
    warningActions.setWarnings({
        warnings: [],
        fieldWarnings: {},
        details: {
            ...warnings,
        },
    }, selectors);
}
function convertActionTemplateParametersToProcessTemplateParameters(actionTemplateParameters: ActionTemplateParameterResource[]): ProcessTemplateParameterResource[] {
    return actionTemplateParameters.map((param) => {
        const { DefaultValue, ...paramWithoutDefaults } = param;
        return {
            ...paramWithoutDefaults,
            // Default values for sensitive and resource-typed parameters can't be imported
            Values: isImportableValueControlType(param.DisplaySettings["Octopus.ControlType"]) && DefaultValue ? [{ Value: convertPropertyValueResourceToString(DefaultValue), Scope: {} }] : [],
            IsOptional: false,
        };
    });
}
export const unsupportedProcessTemplateActionTypes = ["deploy-a-bicep-template", "aws-s3-create-bucket", "aws-ecs", "aws-ecs-update-service"];
export function processTemplateStepsContainAnyUnsupportedActionTypes(steps: DeploymentStepResource[]) {
    return steps.some((s) => s.Actions.some((a) => unsupportedProcessTemplateActionTypes.includes(a.ActionType)));
}
